/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback } from 'react';
import { useByokComplianceSettingWithInitialisation } from '@jira/admin-pages__products-jsm-compliance/src/useByokComplianceSettingWithInitialisation.tsx';
import { useInsightGlobalConfig } from '@jira/servicedesk__insight-global-configuration-store/src/useInsightGlobalConfig.tsx';
import { getLandingPageUrl } from '@jira/servicedesk__insight-common-utils-insight-urls/src/getLandingPageUrl.tsx';
import Lozenge from '@atlaskit/lozenge/lozenge';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { SECTION_ITEM_ASSETS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ASSETS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { L1_MENU_ID } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useSectionItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { L1ExpandableMenuItemSubmenu } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/l1-expandable-menu-item-submenu/main.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { AssetsMenuItems } from '../../common/ui/asset-menu-items/index.tsx';
import { HeaderSchemasMenuItems } from './schemas/index.tsx';
import { SectionActions } from './section-actions/index.tsx';

const upsellLozengeText = defineMessage({
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Premium',
	description:
		'A Lozenge located after Assets label to attract non premium customer explore Assets',
	id: 'navigation-apps-sidebar-nav4-sidebars-content-assets.assets-menu.upsell-lozenge-text',
});

type AssetsMenuProps = {
	isExpanded: boolean;
};

export function AssetsMenu({ isExpanded }: AssetsMenuProps): React.JSX.Element {
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.ASSETS);
	const isSiteAdmin = useIsSiteAdmin();
	const { isByokCustomer } = useByokComplianceSettingWithInitialisation();
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();
	const [
		{ config, dataManager, loading },
		{ fetchGlobalConfig, fetchProvisioningStatus, fetchMyDataManagerRoles },
	] = useInsightGlobalConfig();
	const fetchAssetsConfig = useCallback(async () => {
		fetchGlobalConfig(false);
		fetchProvisioningStatus(false);
		fetchMyDataManagerRoles(false);
	}, [fetchGlobalConfig, fetchMyDataManagerRoles, fetchProvisioningStatus]);
	const menuId = MENU_ID_ASSETS;
	const showActionsMenu = fg('navx-global-side-nav') ? config?.insightAdministrator : true;

	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={(hasExpanded) => {
				if (hasExpanded) {
					sendSectionItemAnalyticsEvent({
						menuId: L1_MENU_ID.ASSETS,
						sectionItem: SECTION_ITEM_ASSETS,
					});
				}
			}}
		>
			<ExpandableMenuItemTrigger
				elemBefore={icon}
				actionsOnHover={
					showActionsMenu ? (
						<SectionActions isInsightAdministrator={config?.insightAdministrator} />
					) : undefined
				}
				testId={getTestId(menuId)}
				onClick={(_e, _a, { isExpanded: hasExpanded }) => {
					if (hasExpanded) {
						sendSectionItemAnalyticsEvent({
							menuId: L1_MENU_ID.ASSETS,
							sectionItem: SECTION_ITEM_ASSETS,
						});
					}
				}}
				interactionName="nav4-sidebar-assets"
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<HeaderSchemasMenuItems />
				<L1ExpandableMenuItemSubmenu
					fetch={fetchAssetsConfig}
					isFetching={loading || dataManager.provisioningLoading || dataManager.myRolesLoading}
					MenuItems={() => (
						<AssetsMenuItems
							dataManagerIsProvisioned={dataManager.isProvisioned}
							dataManagerMyRoles={dataManager.myRoles}
							isSiteAdmin={isSiteAdmin}
							insightAdministrator={config?.insightAdministrator}
							insightReporting={config?.insightReporting}
							isByokCustomer={isByokCustomer}
						/>
					)}
					skeletonCount={2}
				/>
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

type AssetsSingleMenuItemProps = {
	showUpsellLozenge: boolean;
};

export const AssetsSingleMenuItem = ({
	showUpsellLozenge = false,
}: AssetsSingleMenuItemProps): React.JSX.Element => {
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.ASSETS);
	const { formatMessage } = useIntl();
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	return (
		<Nav4MenuLinkItem
			onClick={() => {
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.ASSETS,
					sectionItem: SECTION_ITEM_ASSETS,
				});
			}}
			menuId={MENU_ID_ASSETS}
			elemBefore={icon}
			href={getLandingPageUrl()}
			actionsOnHover={<SectionActions isInsightAdministrator={false} />}
		>
			{`${formattedMessage} `}
			{showUpsellLozenge && (
				<Lozenge
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					testId="navigation-apps-sidebar-nav4-sidebars-content-assets.ui.assets-menu.upsell-lozenge"
					appearance="new"
				>
					{formatMessage(upsellLozengeText)}
				</Lozenge>
			)}
		</Nav4MenuLinkItem>
	);
};
